import React, { useState, useCallback, useEffect } from 'react';
import { Button } from '../atoms/button';
import { Input } from '../atoms/input';
import { Label } from '../atoms/label';
import { Eye, EyeOff } from 'lucide-react';

/**
 * RegisterForm Props
 * 
 * Reusable registration form component extracted from AuthDialog.
 * Contains all registration UI logic, validation, and password strength.
 */
export interface RegisterFormProps {
    /** Callback for email/password registration */
    onRegister: (name: string, email: string, password: string) => Promise<void>;

    /** Callback for Google authentication */
    onGoogleAuth?: () => Promise<void>;

    /** Whether the form is submitting */
    isLoading?: boolean;

    /** Whether this is the web platform (affects sizing) */
    isWeb?: boolean;

    /** Initial error message to display */
    initialError?: string;

    /** Show privacy policy agreement */
    showPrivacyPolicy?: boolean;

    isGoogleButtonTop?: boolean;
}

interface FormData {
    name: string;
    email: string;
    password: string;
}

interface FormErrors {
    name?: string;
    email?: string;
    password?: string;
    general?: string;
}

interface PasswordStrength {
    score: number;
    isValid: boolean;
    className: string;
}

/**
 * RegisterForm
 * 
 * Reusable registration form component with password strength validation.
 * Extracted from AuthDialog to enable reuse across different contexts:
 * - AuthDialog (dialog mode)
 * - RegisterTemplate (full page mode)
 * - Any other component that needs registration functionality
 * 
 * Features:
 * - Name, email, password fields
 * - Email validation
 * - Password strength indicator
 * - Password visibility toggle
 * - Form validation
 * - Error handling
 * - Loading states
 * - Optional privacy policy agreement
 * 
 * Usage:
 * ```tsx
 * <RegisterForm
 *   onRegister={handleRegister}
 *   isLoading={isLoading}
 *   isWeb={true}
 *   showPrivacyPolicy={true}
 * />
 * ```
 */
export const RegisterForm: React.FC<RegisterFormProps> = ({
    onRegister,
    onGoogleAuth,
    isLoading = false,
    isWeb = true,
    initialError,
    showPrivacyPolicy = true,
    isGoogleButtonTop = false,
}) => {
    // Form state
    const [formData, setFormData] = useState<FormData>({
        name: '',
        email: '',
        password: '',
    });

    const [errors, setErrors] = useState<FormErrors>({});
    const [showPassword, setShowPassword] = useState(false);
    const [passwordStrength, setPasswordStrength] = useState<PasswordStrength>({
        score: 0,
        isValid: false,
        className: 'bg-gray-200',
    });

    // Set initial error if provided
    useEffect(() => {
        if (initialError) {
            setErrors({ general: initialError });
        }
    }, [initialError]);

    // Password strength calculation
    const calculatePasswordStrength = useCallback((password: string) => {
        let strength = 0;

        if (password.match(/[a-z]/)) strength += 1; // lowercase
        if (password.match(/[A-Z]/)) strength += 1; // uppercase
        if (password.match(/[0-9]/)) strength += 1; // numbers
        if (password.match(/[^a-zA-Z0-9]/)) strength += 1; // special chars
        if (password.length > 7) strength += 1; // length

        const strengthMap: Record<number, PasswordStrength> = {
            0: { score: 0, isValid: false, className: 'bg-gray-200' },
            1: { score: 1, isValid: false, className: 'bg-red-500' },
            2: { score: 2, isValid: false, className: 'bg-orange-500' },
            3: { score: 3, isValid: false, className: 'bg-yellow-500' },
            4: { score: 4, isValid: false, className: 'bg-lime-500' },
            5: { score: 5, isValid: true, className: 'bg-green-500' },
        };

        const currentStrength = strengthMap[strength] || strengthMap[5];
        setPasswordStrength(currentStrength);
    }, []);

    // Form validation
    const validateForm = useCallback((): boolean => {
        const newErrors: FormErrors = {};

        // Name validation
        if (!formData.name.trim()) {
            newErrors.name = 'Field is required';
        }

        // Email validation
        if (!formData.email.trim()) {
            newErrors.email = 'Field is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = 'Please enter a valid email address';
        }

        // Password validation
        if (!formData.password) {
            newErrors.password = 'Field is required';
        } else if (!passwordStrength.isValid) {
            newErrors.password =
                'The password should include 8 character including (Uppercase - lowercase - Number and symbol)';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    }, [formData, passwordStrength.isValid]);

    // Handle input changes
    const handleInputChange = useCallback(
        (field: keyof FormData, value: string) => {
            setFormData((prev) => ({ ...prev, [field]: value }));

            // Clear specific field error
            if (errors[field]) {
                setErrors((prev) => ({ ...prev, [field]: undefined }));
            }

            // Calculate password strength
            if (field === 'password') {
                calculatePasswordStrength(value);
            }
        },
        [errors, calculatePasswordStrength]
    );

    // Handle form submission
    const handleSubmit = useCallback(
        async (e: React.FormEvent) => {
            e.preventDefault();

            if (!validateForm() || isLoading) return;

            try {
                setErrors({}); // Clear any previous errors
                await onRegister(formData.name, formData.email, formData.password);
            } catch (error: any) {
                // Handle API errors
                setErrors({
                    general: error?.message || 'Registration failed',
                });
            }
        },
        [formData, validateForm, isLoading, onRegister]
    );

    return (
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            {/* Google Sign-in button */}
            {onGoogleAuth && (
                <>
                    <Button
                        type="button"
                        variant="secondary-gray"
                        size={isWeb ? 'xl' : 'xs'}
                        onClick={onGoogleAuth}
                        disabled={isLoading}
                        className="w-full flex items-center justify-center gap-3 font-semibold"
                        data-track="google_oauth"
                    >
                        <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z" fill="#4285F4" />
                            <path d="M9.003 18c2.43 0 4.467-.806 5.956-2.18L12.05 13.56c-.806.54-1.836.86-3.047.86-2.344 0-4.328-1.584-5.036-3.711H.96v2.332C2.44 15.983 5.485 18 9.003 18z" fill="#34A853" />
                            <path d="M3.964 10.712c-.18-.54-.282-1.117-.282-1.71 0-.593.102-1.17.282-1.71V4.96H.957C.347 6.175 0 7.55 0 9.002c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05" />
                            <path d="M9.003 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.464.891 11.428 0 9.003 0 5.485 0 2.44 2.017.96 4.958L3.967 7.29c.708-2.127 2.692-3.71 5.036-3.71z" fill="#EA4335" />
                        </svg>
                        Sign up with Google
                    </Button>
                    <div className="relative flex justify-center text-sm"><span className="text-gray-700">Or create account with your email</span></div>

                </>
            )}
            {/* Name field */}
            <div>
                <Label htmlFor="name" className="block font-medium text-sm mb-[6px] text-gray-700">
                    Name
                </Label>
                <Input
                    id="name"
                    data-testid="input-name"
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    placeholder="Enter your first and last name"
                    className={`${errors.name ? 'border-red-500' : ''} placeholder:text-gray-400 border-gray-300`}
                    disabled={isLoading}
                />
                {errors.name && (
                    <p className="text-sm text-red-500 mt-1" data-testid="error-name">
                        {errors.name}
                    </p>
                )}
            </div>

            {/* Email field */}
            <div>
                <Label htmlFor="email" className="block font-medium text-sm mb-[6px] text-gray-700">
                    Email
                </Label>
                <Input
                    id="email"
                    data-testid="input-email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    placeholder="Enter your email"
                    className={`${errors.email ? 'border-red-500' : ''} placeholder:text-gray-400 border-gray-300`}
                    disabled={isLoading}
                />
                {errors.email && (
                    <p className="text-sm text-red-500 mt-1" data-testid="error-email">
                        {errors.email}
                    </p>
                )}
            </div>

            {/* Password field */}
            <div>
                <Label htmlFor="password" className="block font-medium text-sm mb-[6px] text-gray-700">
                    Password
                </Label>
                <div className="relative">
                    <Input
                        id="password"
                        data-testid="input-password"
                        type={showPassword ? 'text' : 'password'}
                        value={formData.password}
                        onChange={(e) => handleInputChange('password', e.target.value)}
                        placeholder="••••••••"
                        className={`${errors.password ? 'border-red-500' : ''} placeholder:text-gray-400 border-gray-300`}
                        disabled={isLoading}
                    />
                    <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                        disabled={isLoading}
                    >
                        {showPassword ? (
                            <EyeOff size={16} strokeWidth={2} />
                        ) : (
                            <Eye size={16} strokeWidth={2} />
                        )}
                    </button>
                </div>

                {/* Password strength indicator */}
                {formData.password && (
                    <div className="mt-2">
                        <div className="flex gap-1">
                            {Array.from({ length: 5 }, (_, i) => (
                                <div
                                    key={i}
                                    className={`h-2 flex-1 rounded-full transition-colors ${i < passwordStrength.score ? passwordStrength.className : 'bg-gray-200'
                                        }`}
                                />
                            ))}
                        </div>
                        <p className="text-xs text-gray-600 mt-1">
                            The password should include 8 character including (Uppercase - lowercase - Number
                            and symbol)
                        </p>
                    </div>
                )}

                {errors.password && (
                    <p className="text-sm text-red-500 mt-1" data-testid="error-password">
                        {errors.password}
                    </p>
                )}
            </div>

            {/* General error message */}
            {errors.general && (
                <div
                    className="p-3 bg-red-50 border border-red-200 rounded-lg"
                    data-testid="error-general"
                >
                    <p className="text-sm text-red-600">{errors.general}</p>
                </div>
            )}

            {/* Submit button */}
            <Button
                id="btn-submit-register"
                data-testid="btn-submit-auth"
                data-track="register_button"
                type="submit"
                variant="primary"
                size={isWeb ? 'lg' : 'xs'}
                disabled={isLoading}
                className="w-full font-semibold mt-2"
            >
                {isLoading ? (
                    <div className="flex items-center gap-2 justify-center">
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Creating account...
                    </div>
                ) : (
                    'Create account'
                )}
            </Button>

            {/* Privacy policy agreement */}
            {showPrivacyPolicy && (
                <div className="mt-2">
                    <p className="text-sm text-gray-800 text-center font-normal leading-[20px]">
                        By creating an account, I agree with Ideoz's{' '}
                        <a
                            href="https://ideoz.ai/privacy-policy/"
                            target="_blank"
                            className="hover:text-primary-700 font-semibold text-[#1E2340]"
                            data-testid="link-privacy-policy"
                            rel="noopener noreferrer"
                        >
                            privacy policy
                        </a>
                    </p>
                </div>
            )}
        </form>
    );
};
